import { useAppToast } from "@/hooks/useAppToast";
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { AppForm, FormField, FormFieldProps } from "@picovico/ui";
import { catchErrorHandler, parseApiError } from "@picovico/utils";
import { useResetPasswordMutation } from "@store/services/auth";
import useLogout from "hooks/useLogout";
import { isString } from "lodash";
import { useRouter } from "next/router";
import { useAppSelector } from "store/hooks";
import * as yup from "yup";

export interface resetPasswordValues {
  password: string;
  confirm_password: string;
  token: string;
}

interface ResetPasswordFormProps extends BoxProps {
  buttonProps?: ButtonProps;
  formProps?: BoxProps;
  formFieldProps?: FormFieldProps;
  passwordFieldProps?: FormFieldProps;
  confirmPasswordFieldProps?: FormFieldProps;
  loggedInNoticeProps?: BoxProps;
  loggedInMessageProps?: TextProps;
  cancelButtonProps?: ButtonProps;
  logoutButtonProps?: ButtonProps;
}

const resetPasswordFormValidationSchema = yup.object().shape({
  password: yup.string().required().label("New password"),
  confirm_password: yup
    .string()
    .required()
    .oneOf(
      [yup.ref("password")],
      "Confirm new password doesn't match with new password",
    )
    .label("Confirm Password"),
});

const initialValues: resetPasswordValues = {
  password: "",
  confirm_password: "",
  token: "",
};

function ResetPasswordForm({
  formFieldProps,
  buttonProps,
  formProps,
  passwordFieldProps,
  confirmPasswordFieldProps,
  loggedInNoticeProps,
  loggedInMessageProps,
  cancelButtonProps,
  logoutButtonProps,
  ...props
}: ResetPasswordFormProps) {
  const router = useRouter();
  const { isLoggedIn, user } = useAppSelector((state) => state.user);
  const { error: errorToast, success } = useAppToast();
  const [requestPasswordChange, { isLoading, error }] =
    useResetPasswordMutation({});
  const { logout, isLoading: isLoggingOut } = useLogout();
  const token = router.query.token as string;

  // The reset link is meant for a signed-out user; with an active session the
  // reset request gets rejected, so make the user end it explicitly first.
  // The form only renders once logout succeeds and isLoggedIn flips to false.
  if (isLoggedIn) {
    return (
      <Box mt="1.5rem" w="full" {...loggedInNoticeProps}>
        <Text {...loggedInMessageProps}>
          You&apos;re currently logged in
          {user?.email ? ` as ${user.email}` : ""}. To reset your password from
          this link, you need to log out first.
        </Text>
        <Flex gap="1rem" mt="1.5rem" w="full">
          <Button
            flex={1}
            variant="outline"
            bg="transparent"
            color="inherit"
            borderColor="currentColor"
            _hover={{ bg: "blackAlpha.100" }}
            isDisabled={isLoggingOut}
            onClick={() => router.replace("/")}
            {...cancelButtonProps}
          >
            Cancel
          </Button>
          <Button
            flex={1}
            isLoading={isLoggingOut}
            isDisabled={isLoggingOut}
            onClick={logout}
            {...buttonProps}
            {...logoutButtonProps}
          >
            Logout and continue
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <AppForm<resetPasswordValues>
      formProps={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        gap: "1rem",
        mt: "1.5rem",
        ...props,
        ...formProps,
      }}
      apiError={(error && parseApiError(error)) || null}
      initialValues={initialValues}
      validationSchema={resetPasswordFormValidationSchema}
      onSubmit={(values) => {
        requestPasswordChange({
          ...values,
          token,
        })
          .unwrap()
          .then((res) => {
            if (res?.data) {
              success(isString(res.data) ? res?.data : res.data?.message);
              router.push("/login");
            }
          })
          .catch((err) => {
            catchErrorHandler(err, errorToast);
            if (err.status === 401) {
              router.push("/forgot-password");
            }
          });
      }}
    >
      <FormField name="password" type="password" label="New Password" {...formFieldProps} {...passwordFieldProps} />
      <FormField name="confirm_password" type="password" label="Confirm New Password" {...formFieldProps} {...confirmPasswordFieldProps} />
      <Button
        isLoading={isLoading || false}
        isDisabled={isLoading || false}
        my="1rem"
        w="full"
        type="submit"
        {...buttonProps}
      >
        Reset Password
      </Button>
    </AppForm>
  );
}

export default ResetPasswordForm;
